// Ekran 02 — Mal Kabul.
// Sınıf adları ref_files/btys-erp-02-mal-kabul.html'den birebir alındı.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { useSekmeler } from '../../components/Sekmeler'
import { toast } from '../../components/Toast'
import { useFitInsp } from '../../lib/useFitInsp'
import { tr, trGiris, trTarihSaat, sayiyaCevir, trBugun, SAAT_DILIMI } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import NumuneSekmesi from '../lab/NumuneSekmesi'
import { GerekceDrawer, NumuneDrawer } from './MalKabulDrawerlari'
import OnayDrawer from '../../components/OnayDrawer'
import IceAktarDrawer from '../../components/IceAktarDrawer'
import TamSayfaKabul, { DURUM_ETIKET } from './TamSayfaKabul'
import YeniKabulSekmesi from './YeniKabulSekmesi'
import { lotlar, type LotDurumu, type LotOzet } from './api'
import Sayfalayici from '../../components/Sayfalayici'

const SEGMENTLER: { anahtar: LotDurumu | 'tumu'; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'taslak', etiket: 'Taslak' },
  { anahtar: 'onayli', etiket: 'Onaylı' },
  { anahtar: 'reddedildi', etiket: 'Reddedilen' },
  { anahtar: 'storno', etiket: 'Storno' },
]

// Sıra referanstaki `select.sel#ft` ile birebir (ref:346): ay · bugun · hafta.
// İlk seçenek aynı zamanda açılış değeri.
const ZAMAN_SECENEKLERI = [
  { deger: 'ay', etiket: 'Bu ay' },
  { deger: 'bugun', etiket: 'Bugün' },
  { deger: 'hafta', etiket: 'Bu hafta' },
]

/**
 * Taslakta panelden düzenlenebilen alanlar (ref:615-617 + tartım). `havuzId`
 * burada yok — eşleşme yalnız "Yeni Kabul" formunda seçilir.
 */
type DuzenlenebilirAlanlar = {
  plaka: string | null
  surucu: string | null
  brut: number | null
  dara: number | null
  birimFiyat: number | null
}

/** Referans `.pasif`: reddedilen ve storno satırları soluklaşır. */
function pasifMi(l: LotOzet) {
  return l.durum === 'reddedildi' || l.durum === 'storno'
}

/**
 * Gün filtresi TESİS TAKVİM GÜNÜNE göre.
 *
 * Kayan 24/168 saatlik pencere kullanılıyordu: sabah 09:00'da "Bugün" dünün
 * 10:00'undaki kabulü de sayıyordu. Sunucu tarafı (HareketUclari) gerçek TR
 * gününü kullanıyor; iki ekran aynı soruya farklı cevap veriyordu.
 */
function gunFiltresi(l: LotOzet, secim: string): boolean {
  if (secim === 'ay') return true

  const gun = trBugun()
  const kayit = new Date(l.zaman).toLocaleDateString('sv-SE', { timeZone: SAAT_DILIMI })
  if (secim === 'bugun') return kayit === gun

  // "Hafta" = bugün dahil son 7 takvim günü.
  const yediGunOnce = new Date(Date.now() - 6 * 86_400_000)
    .toLocaleDateString('sv-SE', { timeZone: SAAT_DILIMI })
  return kayit >= yediGunOnce && kayit <= gun
}

/** Referans tablo biçimi: "15.08 · 10:12" — yıl yazılmaz. */
function kisaTarih(iso: string): string {
  const tam = trTarihSaat(iso)
  if (tam === '—') return tam
  const [tarih, saat] = tam.split(' ')
  const [g, a] = tarih.split('.')
  return `${g}.${a} · ${saat}`
}

const SAYFA_BOYU = 25

function Icerik() {
  const [durum, setDurum] = useState<LotDurumu | 'tumu'>('tumu')
  const [arama, setArama] = useState('')
  const [zaman, setZaman] = useState('ay')
  const [sayfa, setSayfa] = useState(1)
  const [seciliId, setSeciliId] = useState<string | null>(null)
  const panel = useFitInsp<HTMLDivElement>()
  const { ac } = useDrawer()

  /**
   * Taslak silme. Yalnız taslakta açık: onaylı kayıt K15 gereği silinemez,
   * sunucu da 409 döner — buradaki koşul yalnız gereksiz gidiş dönüşü önlüyor.
   */
  const sil = useMutation({
    mutationFn: (l: LotOzet) => lotlar.sil(l.id).then(() => l),
    onSuccess: (l) => {
      kuyruk.invalidateQueries({ queryKey: ['lotlar'] })
      kuyruk.invalidateQueries({ queryKey: ['lot-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['lot-sayaclari'] })
      if (seciliId === l.id) setSeciliId(null)
      toast(`${l.belgeNo} taslağı silindi`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Taslak silinemedi'),
  })
  const sekmeler = useSekmeler()
  const kuyruk = useQueryClient()
  const { izinVar, stornolayabilir } = useYetki()
  const yazabilir = izinVar('MalKabul', 'yaz')

  const { data: ozet } = useQuery({ queryKey: ['lot-ozeti'], queryFn: lotlar.ozet })
  const { data: sayaclar = [] } = useQuery({
    queryKey: ['lot-sayaclari'],
    queryFn: lotlar.sayaclar,
  })

  const { data: hamListe = [], isLoading } = useQuery({
    queryKey: ['lotlar', durum, arama],
    queryFn: () =>
      lotlar.liste({ durum: durum === 'tumu' ? undefined : durum, q: arama || undefined }),
  })

  // Zaman filtresi istemcide — referans filtered() de böyle yapıyor ve
  // liste tek tesisin kayıtlarıyla sınırlı.
  const liste = hamListe.filter((l) => gunFiltresi(l, zaman))

  const sayfaSayisi = Math.max(1, Math.ceil(liste.length / SAYFA_BOYU))
  const gecerliSayfa = Math.min(sayfa, sayfaSayisi)
  const sayfadakiler = liste.slice((gecerliSayfa - 1) * SAYFA_BOYU, gecerliSayfa * SAYFA_BOYU)

  const sayac = (a: LotDurumu | 'tumu') =>
    a === 'tumu'
      ? sayaclar.reduce((t, s) => t + s.adet, 0)
      : (sayaclar.find((s) => s.durum === a)?.adet ?? 0)

  // Referans açılışta bir satırı seçili getiriyor — panel boş kalmasın.
  const ilkId = sayfadakiler[0]?.id
  useEffect(() => {
    if (ilkId && !liste.some((l) => l.id === seciliId)) setSeciliId(ilkId)
  }, [ilkId, liste, seciliId])

  const { data: detay } = useQuery({
    queryKey: ['lot', seciliId],
    queryFn: () => lotlar.detay(seciliId!),
    enabled: !!seciliId,
  })

  function tazele() {
    kuyruk.invalidateQueries({ queryKey: ['lotlar'] })
    kuyruk.invalidateQueries({ queryKey: ['lot'] })
    kuyruk.invalidateQueries({ queryKey: ['lot-ozeti'] })
    kuyruk.invalidateQueries({ queryKey: ['lot-sayaclari'] })
    kuyruk.invalidateQueries({ queryKey: ['hareketler'] })
  }

  const hata = (h: unknown, varsayilan: string) =>
    toast(h instanceof ApiHatasi ? h.message : varsayilan)

  const onayla = useMutation({
    mutationFn: (id: string) => lotlar.onayla(id),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => hata(h, 'Onaylanamadı'),
  })

  const reddet = useMutation({
    mutationFn: ({ id, gerekce }: { id: string; gerekce: string }) => lotlar.reddet(id, gerekce),
    onSuccess: (l) => { tazele(); toast(`${l.belgeNo} reddedildi — stok etkisi yok`) },
    onError: (h) => hata(h, 'Reddedilemedi'),
  })

  const storno = useMutation({
    mutationFn: ({ id, gerekce }: { id: string; gerekce: string }) => lotlar.storno(id, gerekce),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => hata(h, 'Storno oluşturulamadı'),
  })

  // Taslakta panelin düzenlenebilir tüm alanları tek PUT ile gider — referans
  // da aynı `.fgrid`'i açıyor (ref:615-617), yalnız tartım değil.
  const taslakKaydet = useMutation({
    mutationFn: ({ lot, alanlar }: { lot: LotOzet; alanlar: DuzenlenebilirAlanlar }) =>
      lotlar.guncelle(lot.id, { havuzId: lot.havuzId, ...alanlar }),
    onSuccess: () => { tazele(); toast('Taslak güncellendi') },
    onError: (h) => hata(h, 'Taslak kaydedilemedi'),
  })

  function tamSayfaAc(l: LotOzet) {
    sekmeler.ac({ id: `c-${l.id}`, baslik: l.belgeNo, icerik: <TamSayfaKabul id={l.id} /> })
  }

  /** Rozet referansta analiz sekmesini açar (ref:576 · 629). */
  function numuneSekmesiAc(numuneId: string, no: string) {
    sekmeler.ac({ id: `n-${numuneId}`, baslik: no, icerik: <NumuneSekmesi id={numuneId} /> })
  }

  /**
   * Reddedilen/storno kaydı "yeni kabul olarak kopyala" (ref:643 ·
   * `yenidenOlustur`): kaynak satırın eşleşmesi, aracı ve fiyatı yeni taslak
   * formuna taşınır; tartım ve numune bağı taşınmaz — yeni parti yeniden
   * tartılır.
   */
  function kopyalayarakAc(l: LotOzet) {
    // Sekme kimliği kaynağa özel: `ac()` aynı id'li sekmeyi YENİDEN açmaz,
    // "yeni" kullanılsaydı açık ve doldurulmuş bir form sessizce öne gelir,
    // toast ise kopya açıldığını söylerdi.
    const id = `yeni-${l.id}`
    sekmeler.ac({
      id,
      baslik: `Yeni Kabul · ${l.belgeNo}`,
      icerik: (
        <YeniKabulSekmesi
          sekmeId={id}
          baslangic={{
            havuzId: l.havuzId,
            plaka: l.plaka ?? '',
            surucu: l.surucu ?? '',
            fiyat: trGiris(l.birimFiyat),
          }}
        />
      ),
    })
    toast(`${l.belgeNo} kopyası yeni sekmede açıldı — kaydedilene kadar taslak oluşmaz`)
  }

  return (
    <>
      <div className="crumb">
        Operasyon <b>/ Mal Kabul</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Mal Kabul</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled
            title="Faz 7 kapsamı dışında — dışa aktarma henüz bağlanmadı"
            onClick={() => undefined}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel’e aktar
          </button>
          <button
            className="btn btn-sec"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? 'Excel şablonuyla toplu yükle' : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Mal kabulleri içe aktar',
                altBaslik: 'İrsaliye no anahtardır — var olan kayıt güncellenir',
                icerik: (
                  <IceAktarDrawer
                    baslik="mal kabul"
                    sablonYolu="/api/lotlar/sablon"
                    sablonAdi="btys-mal-kabul-sablonu.xlsx"
                    iceAktarYolu="/api/lotlar/ice-aktar"
                    tazeleAnahtari={['lotlar', 'lot-ozeti']}
                  />
                ),
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            İçe aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              sekmeler.ac({
                id: 'yeni',
                baslik: 'Yeni Kabul',
                icerik: <YeniKabulSekmesi sekmeId="yeni" />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Yeni kabul
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Bugün Kabul</div>
          <div className="k-v">{tr(ozet?.bugunTonaj, 2)}</div>
          {/* `.k-s` referansta yalnız adet taşır (ref:335 · :591); birim bilgisi
              "Kabul" etiketinde zaten örtük — ön ek şeridi diğer dört karttan
              ayırıyordu. */}
          <div className="k-s">{ozet?.bugunKabul ?? '—'} kayıt</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Kabul</div>
          <div className="k-v">{tr(ozet?.buAyTonaj, 2)}</div>
          <div className="k-s">{ozet?.buAyKayit ?? '—'} kayıt</div>
        </div>
        <div className="k">
          <div className="k-l">Bekleyen Taslak</div>
          <div className="k-v amber">{ozet?.taslakBekleyen ?? '—'}</div>
          <div className="k-s">kapatılmamış kayıt</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Reddedilen</div>
          <div className="k-v">{ozet?.redEdilen ?? '—'}</div>
          <div className="k-s">geri çevrilen parti</div>
        </div>
        <div className="k">
          <div className="k-l">Numunesiz Kabul</div>
          <div className="k-v amber">{ozet?.numunesizKabul ?? '—'}</div>
          <div className="k-s">kalite takibi yapılmamış</div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {SEGMENTLER.map((s) => (
                <button
                  key={s.anahtar}
                  type="button"
                  className={durum === s.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => { setDurum(s.anahtar); setSayfa(1) }}
                >
                  {s.etiket}
                  <small>{sayac(s.anahtar)}</small>
                </button>
              ))}
            </div>
            <select
              className="sel"
              value={zaman}
              onChange={(e) => { setZaman(e.target.value); setSayfa(1) }}
            >
              {ZAMAN_SECENEKLERI.map((z) => (
                <option key={z.deger} value={z.deger}>{z.etiket}</option>
              ))}
            </select>
            <div className="f-search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Kabul no, plaka, cari ara…"
                value={arama}
                onChange={(e) => { setArama(e.target.value); setSayfa(1) }}
              />
            </div>
            <div className="toolbar-right">
              <span>{liste.length} kayıt</span>
            </div>
          </div>

          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Kabul</th>
                <th style={{ width: 86 }}>Tarih · Saat</th>
                <th style={{ width: 180 }}>Cari</th>
                <th>Substrat</th>
                <th style={{ width: 88 }}>Plaka</th>
                <th className="num" style={{ width: 76 }}>Net t</th>
                <th style={{ width: 112 }}>Lot</th>
                <th style={{ width: 112 }}>Numune</th>
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td></tr>
              )}
              {!isLoading && liste.length === 0 && (
                <tr><td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Kayıt yok.</td></tr>
              )}
              {sayfadakiler.map((l) => (
                <tr
                  key={l.id}
                  className={[seciliId === l.id ? 'sel' : '', pasifMi(l) ? 'pasif' : '']
                    .filter(Boolean).join(' ')}
                  title={pasifMi(l) ? DURUM_ETIKET[l.durum].metin : undefined}
                  onClick={() => setSeciliId(l.id)}
                  onDoubleClick={() => tamSayfaAc(l)}
                >
                  <td>
                    <span className="kod">{l.belgeNo}</span>
                    {l.durum !== 'onayli' && (
                      <div className="kod-badge">
                        <span className={DURUM_ETIKET[l.durum].sinif}>
                          {DURUM_ETIKET[l.durum].metin}
                        </span>
                      </div>
                    )}
                  </td>
                  {/* `num` mono fontu getirir; referans sağa yaslamayı inline
                      `text-align:left` ile bozuyor (ref:570) — komşu Plaka
                      sütunuyla aynı ailede kalsın diye aynısı yapılıyor. */}
                  <td className="num" style={{ textAlign: 'left' }}>{kisaTarih(l.zaman)}</td>
                  <td>{l.cariUnvan}</td>
                  <td>
                    <div className="cell-name" style={{ fontWeight: 500 }}>
                      {l.stokAd}
                      {l.hij && (
                        <span className="warn-i" title="Hijyenizasyon gerekli (K19)">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="cell-sub">{l.stokKod}</div>
                  </td>
                  <td className="mono" style={{ fontSize: 11.5 }}>{l.plaka ?? '—'}</td>
                  <td className="num">{tr(l.net, 2)}</td>
                  <td>
                    {/*
                      Lot numarası yalnız onaylı satırda basılır — referansta da
                      `lot` alanını sadece "Onaylı" kayıtlar taşıyor (ref :398-406,
                      :788). Taslağın iç kimliği `TSL-xxxxxxxx` bir yer tutucu
                      (kararlar-ek.md "Taslak LOT'ta belge numarası yok"); onu
                      lot gibi göstermek operatörü yanıltırdı.
                    */}
                    {l.durum === 'onayli'
                      ? <span className="kod">{l.belgeNo}</span>
                      : <span style={{ color: '#8A98AB' }}>—</span>}
                  </td>
                  <td>
                    {/*
                      Referansın üç dalı (ref:576): numara varsa rozet, onaylı
                      ama numunesiz kayıtta amber "Numune yok" (KPI şeridindeki
                      "Numunesiz Kabul" sayacının satır karşılığı), diğerlerinde
                      gri tire. Taslakta rozet basılmaz — kalite takibi eksiği
                      ancak kayıt kapandığında bir eksikliktir.
                    */}
                    {l.numuneNo && l.numuneId ? (
                      <span
                        className="numb"
                        title="Analiz sayfasını aç"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          numuneSekmesiAc(l.numuneId!, l.numuneNo!)
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          e.preventDefault()
                          e.stopPropagation()
                          numuneSekmesiAc(l.numuneId!, l.numuneNo!)
                        }}
                      >
                        {l.numuneNo}
                      </span>
                    ) : l.durum === 'onayli' ? (
                      <span className="badge b-amber">Numune yok</span>
                    ) : (
                      <span style={{ color: '#8A98AB' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div className="row-acts">
                      {!l.numuneNo && (l.durum === 'onayli' || l.durum === 'taslak') && (
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={!yazabilir}
                          title={yazabilir ? 'Numune al' : 'Bu modülde yazma yetkiniz yok'}
                          onClick={(e) => {
                            e.stopPropagation()
                            ac({
                              baslik: 'Numune al',
                              altBaslik: `${l.belgeNo} · ${l.stokAd}`,
                              boyut: 'sm',
                              icerik: <NumuneDrawer lot={l} />,
                            })
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 2v7L4 20a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-11V2M7 2h10" />
                          </svg>
                        </button>
                      )}
                      {l.durum === 'taslak' && (
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={!yazabilir}
                          title={yazabilir ? 'Taslağı sil' : 'Bu modülde yazma yetkiniz yok'}
                          onClick={(e) => {
                            e.stopPropagation()
                            // Onay ŞART: kayıt geri gelmiyor.
                            ac({
                              baslik: 'Taslak silinsin mi?',
                              altBaslik: `${l.belgeNo} · ${l.stokAd}`,
                              boyut: 'sm',
                              icerik: (
                                <OnayDrawer
                                  soru={`"${l.belgeNo}" taslağı silinecek.`}
                                  aciklama={
                                    'Geri alınamaz. Taslakta belge numarası, stok hareketi ve ' +
                                    'havuz bakiyesi oluşmadığı için hiçbir kayıt etkilenmez; ' +
                                    'alınmış numune varsa laboratuvarda kalır, yalnız bu ' +
                                    'kabulle bağı kesilir.'
                                  }
                                  onayEtiketi="Sil"
                                  tehlikeli
                                  onayla={() => sil.mutateAsync(l)}
                                />
                              ),
                            })
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
                          </svg>
                        </button>
                      )}
                      <button
                        className="mini-btn"
                        type="button"
                        title="Tam sayfa aç"
                        onClick={(e) => { e.stopPropagation(); tamSayfaAc(l) }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="grid-foot">
            <span>Toplam {sayac('tumu')} kabul · sayfa başına {SAYFA_BOYU} kayıt</span>
            <Sayfalayici gecerli={gecerliSayfa} toplam={sayfaSayisi} git={setSayfa} />
          </div>
        </div>

        <aside className="insp card" ref={panel}>
          {!detay ? (
            <div className="insp-empty">
              Bir kabul seçin —<br />detaylar burada anında görünür.
            </div>
          ) : (
            <IncelemePaneli
              detay={detay}
              yazabilir={yazabilir}
              stornolayabilir={stornolayabilir}
              bekleyen={onayla.isPending || reddet.isPending || storno.isPending}
              kaydediliyor={taslakKaydet.isPending}
              onOnayla={() => onayla.mutate(detay.lot.id)}
              onKaydet={(alanlar) => taslakKaydet.mutate({ lot: detay.lot, alanlar })}
              onNumuneSekmesi={numuneSekmesiAc}
              onKopyala={() => kopyalayarakAc(detay.lot)}
              onReddet={() =>
                ac({
                  baslik: 'Kabulü reddet',
                  altBaslik: `${detay.lot.belgeNo} · stok etkisi olmaz`,
                  boyut: 'sm',
                  icerik: (
                    <GerekceDrawer
                      baslik="Red gerekçesi (zorunlu)"
                      uyari={<>Reddedilen kabul stoğa girmez ve kayıt <b>kilitlenir</b>.</>}
                      butonEtiketi="Reddet"
                      tehlikeli
                      bekliyor={reddet.isPending}
                      onay={(g) => reddet.mutate({ id: detay.lot.id, gerekce: g })}
                    />
                  ),
                })
              }
              onStorno={() =>
                ac({
                  baslik: 'Storno oluştur',
                  altBaslik: `${detay.lot.belgeNo} · ters kayıt üretilir (K15)`,
                  boyut: 'sm',
                  icerik: (
                    <GerekceDrawer
                      baslik="Storno gerekçesi (zorunlu)"
                      uyari={
                        <>
                          Kayıt <b>silinmeyecek</b>. Ters kayıt üretilir, havuz bakiyesi geri
                          alınır ve orijinal satır denetim izinde soluk görünür (K15 · K39).
                        </>
                      }
                      butonEtiketi="Storno oluştur"
                      tehlikeli
                      bekliyor={storno.isPending}
                      onay={(g) => storno.mutate({ id: detay.lot.id, gerekce: g })}
                    />
                  ),
                })
              }
              onNumune={() =>
                ac({
                  baslik: 'Numune al',
                  altBaslik: `${detay.lot.belgeNo} · ${detay.lot.stokAd}`,
                  boyut: 'sm',
                  icerik: <NumuneDrawer lot={detay.lot} />,
                })
              }
              onTamSayfa={() => tamSayfaAc(detay.lot)}
            />
          )}
        </aside>
      </div>
    </>
  )
}

/* ------------------------------------------------------ inceleme paneli */

function IncelemePaneli({
  detay, yazabilir, stornolayabilir, bekleyen, kaydediliyor,
  onOnayla, onKaydet, onReddet, onStorno, onNumune, onNumuneSekmesi, onKopyala, onTamSayfa,
}: {
  detay: import('./api').LotDetay
  yazabilir: boolean
  stornolayabilir: boolean
  bekleyen: boolean
  kaydediliyor: boolean
  onOnayla: () => void
  onKaydet: (alanlar: DuzenlenebilirAlanlar) => void
  onReddet: () => void
  onStorno: () => void
  onNumune: () => void
  onNumuneSekmesi: (numuneId: string, no: string) => void
  onKopyala: () => void
  onTamSayfa: () => void
}) {
  const l = detay.lot
  const taslak = l.durum === 'taslak'
  const [brut, setBrut] = useState('')
  const [dara, setDara] = useState('')
  // Referansta taslakta plaka/şoför/fiyat da yazılabilir (ref:615-617); tartımla
  // aynı PUT'a bindikleri için durumları da burada, aynı kalıpta duruyor.
  const [plaka, setPlaka] = useState('')
  const [surucu, setSurucu] = useState('')
  const [fiyat, setFiyat] = useState('')

  // Seçim değişince alanlar seçili kaydın değerine döner — aksi halde bir
  // önceki kaydın tartımı yeni kayıtta görünürdü.
  useEffect(() => {
    setBrut(trGiris(l.brut))
    setDara(trGiris(l.dara))
    setPlaka(l.plaka ?? '')
    setSurucu(l.surucu ?? '')
    setFiyat(trGiris(l.birimFiyat))
  }, [l.id, l.brut, l.dara, l.plaka, l.surucu, l.birimFiyat])

  const brutSayi = brut.trim() === '' ? null : sayiyaCevir(brut)
  const daraSayi = dara.trim() === '' ? null : sayiyaCevir(dara)
  const fiyatSayi = fiyat.trim() === '' ? null : sayiyaCevir(fiyat)
  const metin = (v: string) => (v.trim() === '' ? null : v.trim())
  const canliNet =
    brutSayi !== null && daraSayi !== null && brutSayi > daraSayi
      ? (brutSayi - daraSayi) / 1000
      : null
  const net = taslak ? canliNet : l.net
  const degisti =
    brutSayi !== l.brut ||
    daraSayi !== l.dara ||
    fiyatSayi !== l.birimFiyat ||
    metin(plaka) !== l.plaka ||
    metin(surucu) !== l.surucu

  const d = DURUM_ETIKET[l.durum]

  return (
    <>
      <div className="insp-h">
        <div className="insp-eyebrow">
          <span className="kod">{l.belgeNo}</span>
          <span>·</span>
          <span>{trTarihSaat(l.zaman)}</span>
          <span>·</span>
          <span>{l.cariUnvan}</span>
        </div>
        <div className="insp-t">
          {l.stokAd} <span className={d.sinif}>{d.metin}</span>{' '}
          {l.hij && <span className="badge b-amber">Hijyen</span>}
        </div>
        {l.redGerekce && (
          <div
            className="cell-sub"
            style={{ marginTop: 4, color: l.durum === 'reddedildi' ? 'var(--red)' : 'var(--amber)' }}
          >
            {l.redGerekce}
          </div>
        )}
      </div>

      <div className="insp-b">
        <div className="insp-sec">
          <div className="insp-sec-t">{taslak ? 'Tartım — düzenlenebilir' : 'Tartım'}</div>
          <div className="fgrid">
            <div className="fld">
              <label>Brüt (kg)</label>
              <input
                className="in mono"
                readOnly={!taslak}
                value={taslak ? brut : tr(l.brut, 0)}
                onChange={(e) => setBrut(e.target.value)}
              />
            </div>
            <div className="fld">
              <label>Dara (kg)</label>
              <input
                className="in mono"
                placeholder="—"
                readOnly={!taslak}
                value={taslak ? dara : tr(l.dara, 0)}
                onChange={(e) => setDara(e.target.value)}
              />
            </div>
          </div>
          <div className="netbar">
            <span className="nb-l">Net</span>
            <b>{tr(net, 3)}</b>
            <small>ton · brüt − dara</small>
          </div>
          {taslak && (
            <>
              <div className="fld-hint" style={{ marginTop: 6 }}>
                Kantar entegrasyonu Faz 2 (K7) — tartımlar manuel girilir.
              </div>
              {degisti && (
                <button
                  className="btn btn-sec btn-sm"
                  type="button"
                  style={{ marginTop: 6, width: '100%' }}
                  disabled={!yazabilir || kaydediliyor}
                  title="Tartım ve Bilgiler tek kayıtta güncellenir"
                  onClick={() =>
                    onKaydet({
                      plaka: metin(plaka),
                      surucu: metin(surucu),
                      brut: brutSayi,
                      dara: daraSayi,
                      birimFiyat: fiyatSayi,
                    })
                  }
                >
                  Kaydet
                </button>
              )}
            </>
          )}
        </div>

        <div className="insp-sec">
          <div className="insp-sec-t">Bilgiler</div>
          {/*
            Üç alan da taslakta yazılabilir (ref:615-617) ve "Kaydet" ile aynı
            PUT'a biner. Onaylı/reddedilen/storno kayıtta salt-okunur — K15.
          */}
          <div className="fgrid">
            <div className="fld">
              <label>Plaka</label>
              <input
                className="in mono"
                placeholder="42 XX 000"
                readOnly={!taslak}
                value={taslak ? plaka : (l.plaka ?? '—')}
                onChange={(e) => setPlaka(e.target.value)}
              />
            </div>
            <div className="fld">
              <label>Şoför</label>
              <input
                className="in"
                placeholder="Ad Soyad"
                readOnly={!taslak}
                value={taslak ? surucu : (l.surucu ?? '—')}
                onChange={(e) => setSurucu(e.target.value)}
              />
            </div>
            <div className="fld">
              <label>Birim fiyat (₺/t)</label>
              <input
                className="in mono"
                placeholder="—"
                readOnly={!taslak}
                value={taslak ? fiyat : tr(l.birimFiyat, 2)}
                onChange={(e) => setFiyat(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="insp-sec">
          <div className="inst">
            <div className="inst-t">Lot teorik potansiyeli</div>
            <div className="inst-3">
              <div className="inst-c">
                <div className="inst-v">{tr(detay.potansiyel.biyogaz, 0)}</div>
                <div className="inst-l">Nm³ biyogaz</div>
              </div>
              <div className="inst-c">
                <div className="inst-v">{tr(detay.potansiyel.metan, 0)}</div>
                <div className="inst-l">Nm³ metan</div>
              </div>
              <div className="inst-c">
                <div className="inst-v">{tr(detay.potansiyel.elektrik, 0)}</div>
                <div className="inst-l">kWh elektrik</div>
              </div>
            </div>
          </div>
        </div>

        <div className="insp-sec">
          <div className="insp-sec-t">Lot &amp; Numune</div>
          <div className="mini-list">
            <div className="mini-li">
              Lot
              <span className="vv">{l.durum === 'onayli' ? l.belgeNo : 'onayda oluşur'}</span>
            </div>
            <div className="mini-li">
              Numune
              {l.numuneNo && l.numuneId ? (
                <span
                  className="numb"
                  style={{ marginLeft: 'auto' }}
                  title="Analiz sayfasını aç"
                  role="button"
                  tabIndex={0}
                  onClick={() => onNumuneSekmesi(l.numuneId!, l.numuneNo!)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    e.preventDefault()
                    onNumuneSekmesi(l.numuneId!, l.numuneNo!)
                  }}
                >
                  {l.numuneNo}
                </span>
              ) : l.durum === 'onayli' || taslak ? (
                <span className="badge b-amber" style={{ marginLeft: 'auto' }}>Alınmadı</span>
              ) : (
                <span className="vv">—</span>
              )}
            </div>
            {l.durum === 'onayli' && (
              <div className="mini-li">
                Stok havuzu<span className="vv plus">güncellendi ✓</span>
              </div>
            )}
            {/*
              HAO satırı ekran 09'un LOT paneliyle aynı desende (ref-09:563):
              "Bilgiler" ızgarasında bir `.fld` olarak durması referansta yoktu
              ve dördüncü alan olarak "Belge no"nun yerini almıştı.
              `haoSonrasi` yoksa satır hiç basılmaz.
            */}
            {detay.haoSonrasi != null && (
              <div className="mini-li">
                Havuz HAO (K6)
                <span className="vv">
                  {tr(detay.haoOncesi, 1)} → <b>{tr(detay.haoSonrasi, 1)}</b> ₺/t
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="insp-f">
        {taslak && (
          <button
            className="btn btn-danger btn-sm"
            type="button"
            disabled={!yazabilir || bekleyen}
            onClick={onReddet}
          >
            Reddet
          </button>
        )}
        {l.durum === 'onayli' && (
          <button
            className="btn btn-danger btn-sm"
            type="button"
            disabled={!stornolayabilir || bekleyen}
            title={stornolayabilir ? undefined : 'K15: storno için ayrıca yetkilendirilmeniz gerekir'}
            onClick={onStorno}
          >
            Storno
          </button>
        )}
        {/*
          "Tam sayfa" yalnız TASLAK dalında ikon düğme (ref:635): o dalda altlıkta
          dört düğme yan yana durur, metin sığmaz. Diğer iki dal üç düğmeyle
          kaldığı için referans da metin kullanıyor (ref:640 · :643).
        */}
        {taslak ? (
          <button
            className="btn btn-sec btn-sm"
            type="button"
            title="Tam sayfa"
            style={{ padding: '0 8px' }}
            onClick={onTamSayfa}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
            </svg>
          </button>
        ) : (
          <button className="btn btn-sec btn-sm" type="button" onClick={onTamSayfa}>
            Tam sayfa
          </button>
        )}
        {/*
          İkincil "Numune al" yalnız taslakta (ref:636). Onaylı+numunesiz kayıtta
          referans onu birincil yuvaya taşıyor — aşağıdaki dala bakın; ikisi
          birden basılırsa aynı eylem altlıkta iki kez görünürdü.
        */}
        {taslak && !l.numuneNo && (
          <button
            className="btn btn-sec btn-sm"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={onNumune}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 2v7.5L4.5 19a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 9.5V2M8.5 2h7" />
            </svg>
            Numune al
          </button>
        )}
        {/*
          Altlığın `flex:1` yuvası referansta dört dallı (ref:637-644): taslak →
          Onayla, onaylı+numunesiz → Numune al (kaydın tek açık eylemi, bu yüzden
          `btn-pri`), onaylı+numuneli → Kabul fişi (`btn-sec`; fiş ucu yok, bu
          yüzden devre dışı), reddedilen/storno → kopyala.
        */}
        {taslak ? (
          <button
            className="btn btn-pri btn-sm"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={!yazabilir || bekleyen}
            title={net == null ? 'Onay için brüt ve dara tartımı zorunludur' : undefined}
            onClick={onOnayla}
          >
            Onayla → Lot oluştur
          </button>
        ) : l.durum === 'onayli' && !l.numuneNo ? (
          <button
            className="btn btn-pri btn-sm"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={onNumune}
          >
            Numune al
          </button>
        ) : l.durum === 'onayli' ? (
          <button
            className="btn btn-sec btn-sm"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled
            title="Fiş yazdırma henüz bağlanmadı"
          >
            Kabul fişi
          </button>
        ) : (
          <button
            className="btn btn-pri btn-sm"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={!yazabilir}
            title={
              yazabilir
                ? 'Eşleşme, araç ve fiyat yeni bir taslağa kopyalanır'
                : 'Bu modülde yazma yetkiniz yok'
            }
            onClick={onKopyala}
          >
            Yeni kabul olarak kopyala
          </button>
        )}
      </div>
    </>
  )
}

export default function MalKabulSayfasi() {
  return (
    <Kabuk listeBasligi="Mal Kabul">
      <YetkiKapisi modul="MalKabul">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
